import React from "react";
import { FormattedMessage } from "react-intl";

import {
  BlockModel,
  ReferenceBlockModel,
  RichtextBlockModel,
} from "@courses-models/course";

import { getIcon } from "@apps/viewer/src/assets/icons";
import { colors } from "@apps/viewer/src/ts/config";
import { Store } from "@apps/viewer/src/ts/store";
import { MathJaxUtil } from "@courses-pm2/mathjax-util-component/src/ts";

export type BlockProps = {
  /**
   * A reference to the store that's backing the application lifecycle state.
   */
  store: Store;

  /**
   * A reference to the model representing the block that's currently being
   * rendered.
   */
  model: BlockModel;
};

/**
 * Primary root of the render tree for the content of a single block.
 *
 * This component will render generic content (ie. the teacher note on top, if
 * any was defined) and then propagate rendering of the actual content to the
 * designated block sub components.
 */
export abstract class Block<
  P extends BlockProps,
  S = {}
> extends React.PureComponent<P, S> {
  public componentDidMount(): void {
    // Determine title of the page
    const title = [this.props.model.title, this.props.store.course.title]
      .filter((str) => str)
      .join(" / ");

    const titleNode = document.head.querySelector("title");

    if (titleNode) {
      titleNode.textContent = title;
    }
  }

  /**
   * Primary render method.
   */
  public render(): React.JSX.Element {
    return (
      <article>
        <MathJaxUtil>
          {this.renderTeacherNote()}
          {this.renderTitle()}
          {this.renderContent()}
        </MathJaxUtil>
      </article>
    );
  }

  /**
   * Renderer that'll render the title of the block
   */
  protected renderTitle(): React.ReactNode {
    // We don't want to render the title on RichText blocks, bail out!
    if (
      this.props.model instanceof RichtextBlockModel ||
      this.props.model instanceof ReferenceBlockModel
    ) {
      return undefined;
    }

    return (
      <h2>
        {this.props.model.title}

        <style jsx>{`
          h2 {
            /** Override defaults */
            margin: 0;

            /** Setup font */
            font-size: 36px;
            font-weight: 500;
            line-height: 1;
            text-transform: uppercase;
            color: ${colors.black};
          }
        `}</style>
      </h2>
    );
  }

  /**
   * Renderer that'll inject the teacher note above the actual content within
   * the page.
   */
  protected renderTeacherNote(): React.JSX.Element | undefined {
    const { model } = this.props;

    // Bail out if no teacher note should be displayed
    if (!model.note.content || !model.note.enabled) {
      return undefined;
    }

    // Enable new line to <br />, so that line breaks are actually visible
    // in the output
    const content = model.note.content.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));

    // ... Otherwise render the note as plain text
    return (
      <div className="teacherNote">
        <h6>
          <i className="icon">
            {getIcon("teacherNote", { strokeColor: colors.black })}
          </i>

          <FormattedMessage
            id="Block__teacherNote"
            defaultMessage="Note fra din lærer"
          />
        </h6>

        {content}

        <style jsx>{`
          .teacherNote {
            /**
                         * Apply negative top margin, so that we align with the
                         * visually weighted top of the icon.
                         */
            margin-top: -3px;

            /**
                         * Render the teacherNote using a solid background, and
                         * apply internal padding.
                         */
            background-color: #f8f2d4;
            padding: 25px 30px;

            /** Seperate teacherNote from other content */
            margin-bottom: 30px;
          }

          h6 {
            /**
                         * Fixate height to of entire node to the size of the
                         * icon (we're using a higher line-height value to
                         * visually center the text vertically around the icon).
                         */
            height: 25px;
            overflow: hidden;

            /** Reset margins */
            margin: 0 0 10px;
            padding: 0;

            /** Apply font styling */
            font-size: 18px;
            font-weight: 500;
            line-height: 31px;

            /** Apply icon styling */
            & i {
              /** Set up sizing */
              float: left;
              width: 21px;
              height: 25px;

              /** Apply margin between icon and text */
              margin-right: 10px;

              /** Apply custom color of icon pin */
              & > :global(.svgIcon > svg > .teacherNote__st1) {
                stroke: #dc4320;
              }
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Implement a renderer that'll render the actual content of the block (this
   * will be placed below the teacher note itself).
   */
  protected abstract renderContent(): React.JSX.Element;
}
